import { useState, type FormEvent, type ReactNode } from "react";
import type { HandoffTokenView, Me } from "../api/types.js";
import { can } from "@sts/schemas";
import { Async, Failure } from "../components/Async.jsx";
import { CopyField } from "../components/CopyField.jsx";
import { usePortal } from "../app/context.js";
import { useAsync } from "../app/use-async.js";
import { useSubmit } from "../app/use-submit.js";
import { longDate } from "../lib/format.js";

/**
 * The credential another of this company's systems reads the hand-off feed
 * with.
 *
 * WHY THIS SCREEN EXISTS. The API has been able to mint these since migration
 * 0059 and the portal had no way to ask for one, so the answer to "what do I
 * send to portal.scannix.co?" was: nothing, there is no way to get it. A
 * route nobody can reach is a feature nobody has.
 *
 * WHAT THE CREDENTIAL IS. Not a per-survey code. portal.scannix.co POLLS
 * `GET /handoffs` with this one standing token and raises an enquiry for each
 * survey it finds — reference and address, nothing else. Pressing "Make the
 * hand-off file" on a survey is the other half and a different thing: a file
 * to hand over by hand. This is the standing connection between the two
 * systems, set up once.
 */
export function HandoffConnection(): ReactNode {
  const { client } = usePortal();
  const { state, reload } = useAsync<{ tokens: HandoffTokenView[]; me: Me }>(
    async () => {
      const [tokens, me] = await Promise.all([client.handoffTokens(), client.me()]);
      return { tokens, me };
    },
    [client],
  );

  return (
    <>
      <div className="page-header">
        <h1>Hand-off connection</h1>
        <p className="lede">
          The credential portal.scannix.co reads this organisation&rsquo;s
          surveys with. Set up once, not per survey.
        </p>
      </div>
      {/*
        SAID BEFORE THE BUTTON, not after the mistake. Four credentials were
        minted and two revoked in one afternoon by somebody looking for the
        import code: both are 32 random bytes in base64url, 43 characters, and
        nothing on screen told them apart. Pasting this one into "Import from
        Scannix" fails with a flat "that code is not valid any more", which
        reads like an expiry and sends you back here to mint another.
      */}
      <div className="notice warn" role="note">
        <p>
          <strong>This is not the code for &ldquo;Import from Scannix&rdquo;.</strong>{" "}
          That one releases a single survey and is read off the survey itself:
          open it, then <strong>Hand it to the designer &rarr; Get the handover
          code</strong>. The credential below is the standing connection for
          the whole organisation, so portal.scannix.co can collect surveys
          without anybody pasting anything. The two look alike and are not
          interchangeable.
        </p>
      </div>

      <Async state={state} what="the hand-off connection" reload={reload}>
        {(page) => <Body tokens={page.tokens} me={page.me} reload={reload} />}
      </Async>
    </>
  );
}

function Body({
  tokens,
  me,
  reload,
}: {
  readonly tokens: readonly HandoffTokenView[];
  readonly me: Me;
  readonly reload: () => void;
}): ReactNode {
  const { client } = usePortal();
  const { submit, busy, error } = useSubmit();
  const [label, setLabel] = useState("portal.scannix.co");
  /** Held in state because the server cannot produce it a second time. */
  const [minted, setMinted] = useState<string | null>(null);

  // Minting a standing credential for the whole organisation is an owner's
  // decision, and the API says so too; asking first means the form is not
  // offered to somebody who would only be refused.
  const canMint = can({ role: me.role, resource: "org-settings", action: "update" });

  const live = tokens.filter((t) => t.revokedAt === null);

  const mint = async (event: FormEvent): Promise<void> => {
    event.preventDefault();
    await submit(async () => {
      const created = await client.mintHandoffToken(label.trim());
      setMinted(created.secret);
      reload();
    });
  };

  const revoke = async (id: string): Promise<void> => {
    await submit(async () => {
      await client.revokeHandoffToken(id);
      reload();
    });
  };

  return (
    <>
      {minted === null ? null : (
        <div className="notice" role="status">
          <p>
            <strong>Here it is — this once.</strong> The server keeps only a
            fingerprint of this credential, so it cannot be shown again. Put it
            into portal.scannix.co now; if it is lost, revoke it below and mint
            another.
          </p>
          <CopyField
            label="The hand-off credential"
            value={minted}
            copies="hand-off credential"
            hint="portal.scannix.co sends this to GET /handoffs on api.scannix.co. It can read nothing else."
          />
          <p className="row">
            <button type="button" className="secondary" onClick={() => setMinted(null)}>
              I have saved it
            </button>
          </p>
        </div>
      )}

      <section>
        <h2>Credentials</h2>
        {live.length === 0 ? (
          <div className="state state--empty">
            <p className="state__title">No connection yet</p>
            <p className="state__body">
              Nothing is polling this organisation&rsquo;s surveys. Until a
              credential exists and portal.scannix.co holds it, surveys stay
              here and are handed over as files.
            </p>
          </div>
        ) : (
          <ul className="plain-list" data-testid="handoff-tokens">
            {live.map((token) => (
              <li key={token.id} className="row-between">
                <span>
                  <strong>{token.label}</strong>
                  <span className="small muted">
                    {" "}
                    — made {longDate(token.createdAt)}
                    {token.lastUsedAt === null
                      ? ". Never used, so nothing is polling with it yet."
                      : `. Last used ${longDate(token.lastUsedAt)}.`}
                  </span>
                </span>
                {canMint ? (
                  <button
                    type="button"
                    className="secondary"
                    disabled={busy}
                    onClick={() => void revoke(token.id)}
                  >
                    Revoke
                  </button>
                ) : null}
              </li>
            ))}
          </ul>
        )}
      </section>

      {canMint ? (
        <section>
          <h2>Make a credential</h2>
          <p className="small muted">
            One per receiving system, named so a revoked one can be told from a
            live one. It can only read the list of this organisation&rsquo;s
            surveys — reference and address. It carries no user and cannot
            change anything.
          </p>
          <form className="stacked" onSubmit={(e) => void mint(e)}>
            <div className="field">
              <label htmlFor="handoff-label">What it is for</label>
              <input
                id="handoff-label"
                type="text"
                value={label}
                onChange={(e) => setLabel(e.target.value)}
              />
            </div>
            <button type="submit" disabled={busy || label.trim() === ""}>
              {busy ? "Making…" : "Make the credential"}
            </button>
          </form>
          <Failure error={error} what="that credential" />
        </section>
      ) : (
        <p className="small muted">
          Only an owner or an admin can make or revoke a hand-off credential.
        </p>
      )}
    </>
  );
}
